import type { FC } from "react";
import { Link } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useLinkRender } from "../Link/routerLink.js";

export interface TabItem {
  id: string;
  label: string;
  /**
   * Renders the tab as a link (client-side routed via the app's react-aria
   * `RouterProvider`). Tabs without an `href` are buttons driven by
   * `onSelectTabId`.
   */
  href?: string;
  isDisabled?: boolean;
}

export interface TabsProps {
  tabs: readonly TabItem[];
  selectedTabId: string | undefined;
  onSelectTabId?: (tabId: string) => void;
  ariaLabel?: string;
  className?: string;
}

const tabClassName = (isSelected: boolean, isDisabled: boolean | undefined) =>
  twMerge(
    "flex min-h-8 min-w-0 shrink-0 items-center border-b-2 px-1 pt-px text-xs leading-5 font-normal whitespace-nowrap transition-colors",
    // Inset so the strip's overflow-x-auto doesn't clip the focus ring.
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset",
    isSelected
      ? "border-foreground text-foreground"
      : "border-transparent text-muted-foreground hover:text-foreground",
    isDisabled &&
      "cursor-not-allowed text-muted-foreground opacity-50 hover:text-muted-foreground",
  );

/**
 * The page header's tab strip: underlined items in a horizontally scrollable
 * row. These are navigation tabs — each one is its own URL — so items render
 * as links marked with `aria-current` rather than an ARIA tablist.
 */
export const Tabs: FC<TabsProps> = ({
  tabs,
  selectedTabId,
  onSelectTabId,
  ariaLabel = "Tabs",
  className,
}) => {
  const renderLink = useLinkRender();

  return (
    <nav
      aria-label={ariaLabel}
      className={twMerge(
        "flex min-h-8 min-w-0 items-center gap-4 overflow-x-auto",
        className,
      )}
    >
      {tabs.map((tab) => {
        const isSelected = tab.id === selectedTabId;
        const label = (
          <span className="block max-w-40 truncate">{tab.label}</span>
        );

        return tab.href !== undefined ? (
          <Link
            key={tab.id}
            href={tab.href}
            aria-current={isSelected ? "page" : undefined}
            isDisabled={tab.isDisabled}
            render={tab.isDisabled ? undefined : renderLink}
            className={tabClassName(isSelected, tab.isDisabled)}
          >
            {label}
          </Link>
        ) : (
          <button
            key={tab.id}
            type="button"
            aria-current={isSelected ? "page" : undefined}
            disabled={tab.isDisabled}
            onClick={() => {
              onSelectTabId?.(tab.id);
            }}
            className={twMerge(
              tabClassName(isSelected, tab.isDisabled),
              !tab.isDisabled && "cursor-pointer",
            )}
          >
            {label}
          </button>
        );
      })}
    </nav>
  );
};
